import type { $Fetch } from 'ofetch';
import type {
  Noticia, UpsertNoticiaCommand,
  NotasMultidisciplinar, CpaRelatorioAnalitico,
} from '@/domain/model/AcadGaps';
import type {
  NoticiaGateway, NotasMultidisciplinarGateway, CpaRelatorioAnaliticoGateway,
} from '@/application/port/out/AcadGapsGateways';
import type { ApiResponse } from '@/infrastructure/http/types';
import { assertApiSuccess } from '@/infrastructure/http/assertApiSuccess';

/** Notícias broadcast. Authorities `comunicacao:noticia:read|manage`. */
export class HttpNoticiaGateway implements NoticiaGateway {
  private readonly path = '/api/v1/comunicacao/noticias';
  constructor(private readonly client: $Fetch) {}
  async list(): Promise<Noticia[]> {
    const res = await this.client<ApiResponse<Noticia[]>>(this.path, { method: 'GET' });
    assertApiSuccess(res); return res.data;
  }
  async create(cmd: UpsertNoticiaCommand): Promise<Noticia> {
    const res = await this.client<ApiResponse<Noticia>>(this.path, { method: 'POST', body: cmd });
    assertApiSuccess(res); return res.data;
  }
  async update(id: string, cmd: UpsertNoticiaCommand): Promise<Noticia> {
    const res = await this.client<ApiResponse<Noticia>>(`${this.path}/${encodeURIComponent(id)}`, { method: 'PUT', body: cmd });
    assertApiSuccess(res); return res.data;
  }
  async remove(id: string): Promise<void> {
    await this.client<void>(`${this.path}/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }
}

/** Notas multidisciplinar (matriz da turma, read-only). Authority `enrollment:aluno:read`. */
export class HttpNotasMultidisciplinarGateway implements NotasMultidisciplinarGateway {
  constructor(private readonly client: $Fetch) {}
  async get(cohortId: string): Promise<NotasMultidisciplinar> {
    const res = await this.client<ApiResponse<NotasMultidisciplinar>>(
      `/api/v1/turmas/${encodeURIComponent(cohortId)}/notas-multidisciplinar`, { method: 'GET' });
    assertApiSuccess(res); return res.data;
  }
}

/** CPA — relatório analítico. Authority `acadadmin:cpa:ver_relatorio`. */
export class HttpCpaRelatorioAnaliticoGateway implements CpaRelatorioAnaliticoGateway {
  constructor(private readonly client: $Fetch) {}
  async get(cicloId: string): Promise<CpaRelatorioAnalitico> {
    const res = await this.client<ApiResponse<CpaRelatorioAnalitico>>(
      `/api/v1/cpa/ciclos/${encodeURIComponent(cicloId)}/relatorio/analitico`, { method: 'GET' });
    assertApiSuccess(res); return res.data;
  }
}
